<template>
  <div class="storyboard-generation-view">
    <section v-if="sceneNavigatorItems.length" class="storyboard-generation-flow">
      <div class="storyboard-generation-flow__head">
        <div>
          <span class="storyboard-generation-flow__eyebrow">连续处理</span>
          <strong>
            当前处理镜头 {{ selectedSceneIndex + 1 }} / {{ sceneNavigatorItems.length }}
            <template v-if="selectedSceneItem"> · {{ selectedSceneItem.title }}</template>
          </strong>
          <p>
            生成区始终围绕当前镜头工作；处理完成后可直接切到上下一个镜头，保持连续操作。
          </p>
        </div>
        <div class="storyboard-generation-flow__actions">
          <el-button plain :disabled="!previousSceneNumber" @click="previousSceneNumber && emit('select-scene', previousSceneNumber)">上一个镜头</el-button>
          <el-button plain :disabled="!nextSceneNumber" @click="nextSceneNumber && emit('select-scene', nextSceneNumber)">下一个镜头</el-button>
        </div>
      </div>

      <div class="storyboard-generation-flow__list">
        <button
          v-for="item in sceneNavigatorItems"
          :key="item.scene_number"
          type="button"
          class="storyboard-generation-flow__item"
          :class="{ 'storyboard-generation-flow__item--active': item.scene_number === activeSceneNumber }"
          @click="emit('select-scene', item.scene_number)"
        >
          <span>镜头{{ item.scene_number }}</span>
          <strong>{{ item.title }}</strong>
          <small>{{ getSceneStatusLabel(item) }}</small>
        </button>
      </div>
    </section>

    <div v-if="scene" class="storyboard-generation-layout">
      <div class="storyboard-generation-main">
        <div class="storyboard-generation-preview-shell">
          <div class="storyboard-generation-preview__chrome">
            <div class="storyboard-generation-preview__meta">
              <span class="storyboard-generation-preview__eyebrow">{{ previewStageLabel }}</span>
              <strong>{{ previewHeadline }}</strong>
              <small>{{ previewSubline }}</small>
            </div>
            <div class="storyboard-generation-preview__chips">
              <span>镜头 {{ scene.scene_number }}</span>
              <span>{{ activeSceneStatusLabel }}</span>
              <span>{{ boundAssetsList.length }} 项绑定</span>
            </div>
          </div>

          <div class="storyboard-generation-preview" :class="{ 'storyboard-generation-preview--busy': previewOverlayVisible }">

            <template v-if="generationTab === 'image'">
              <img
                v-if="selectedImageUrl && !selectedImageFailed"
                :key="selectedImagePreviewKey"
                :src="selectedImageUrl"
                :alt="`scene-${scene.scene_number}`"
                @error="handleSelectedImageError"
              />
              <div v-else class="storyboard-generation-preview__placeholder">
                <div>
                  <strong>{{ selectedImageUrl ? '当前图片暂不可访问' : '当前镜头还没有图片' }}</strong>
                  <p>{{ selectedImageUrl ? '当前图片链接无法打开。通常是素材中心里的 OSS 地址失效、权限关闭，或原始媒体已下线。' : '先补充图片创意，再选择素材、上传图片或直接生成当前镜头画面。' }}</p>
                </div>
              </div>
            </template>

            <template v-else>
              <video
                v-if="stableSelectedVideoUrl"
                :src="stableSelectedVideoUrl"
                controls
                playsinline
                preload="metadata"
                @loadeddata="emit('scene-video-loaded', scene.scene_number)"
                @error="handleSelectedVideoError(scene.scene_number)"
              />
              <div v-else class="storyboard-generation-preview__placeholder">
                <div>
                  <strong>当前镜头还没有视频</strong>
                  <p>可以基于当前分镜图片和视频提示词生成，也可以上传人工处理视频。</p>
                </div>
              </div>
            </template>

            <div v-if="previewOverlayVisible" class="storyboard-generation-preview__task-overlay">
              <div class="storyboard-generation-preview__task-panel">
                <span class="storyboard-generation-preview__task-eyebrow">
                  {{ generationTab === 'image' ? '镜头图片' : '镜头视频' }}
                </span>
                <div class="storyboard-generation-preview__task-spinner" />
                <strong>{{ previewOverlayTitle }}</strong>
                <p>{{ previewOverlayDescription }}</p>
                <small v-if="previewOverlayNote">{{ previewOverlayNote }}</small>
              </div>
            </div>
          </div>
        </div>

        <div class="storyboard-generation-editor">
          <template v-if="generationTab === 'image'">
            <div class="storyboard-generation-editor__grid">
              <section v-if="showImageSummaryCard" class="storyboard-editor-card storyboard-editor-card--summary">
                <div class="storyboard-editor-card__head">
                  <div>
                    <label>镜头摘要</label>
                    <span>先对齐这一镜头到底要表现什么</span>
                  </div>
                </div>
                <div class="storyboard-editor-card__summary">
                  {{ activeSceneSummary || '当前镜头还没有补充描述' }}
                </div>
              </section>

              <section class="storyboard-editor-card storyboard-editor-card--prompt" :class="{ 'storyboard-editor-card--wide': !showImageSummaryCard }">
                <div class="storyboard-editor-card__head">
                  <div>
                    <label>图片创意描述</label>
                    <span>把光线、构图、主体状态和情绪都集中在这里编辑</span>
                  </div>
                </div>
                <PromptAssistTextarea
                  v-model="scene.background_prompt"
                  field-label="图片创意描述"
                  assistant-title="图片提示词助手"
                  assistant-description="把你想额外补强的光线、镜头质感、构图重点或画面情绪写进去，系统会基于当前内容整理成更适合出图的一版文本。"
                  :project-id="projectId"
                  stage="storyboard"
                  purpose="image_prompt"
                  :scene-number="scene.scene_number"
                  :rows="7"
                  :maxlength="1500"
                  placeholder="补充画面构图、镜头质感、主体状态和光影要求"
                  :mention-items="mentionItems"
                  allow-create-mention
                  @input="emit('mark-dirty')"
                  @create-mention-item="emit('create-mention-asset', $event)"
                />
              </section>

              <section
                v-if="boundAssetsList.length"
                class="storyboard-editor-card storyboard-editor-card--wide storyboard-editor-card--bindings"
              >
                <div class="storyboard-editor-card__head">
                  <div>
                    <label>当前官方绑定</label>
                    <span>生成时会优先遵守这些角色、场景和道具设定</span>
                  </div>
                </div>
                <div class="generation-side-card__binding-assets">
                  <article
                    v-for="asset in boundAssetsList"
                    :key="asset.id"
                    class="generation-side-card__binding-asset"
                  >
                    <img v-if="asset.image_url && !failedBoundAssets[asset.id]" :src="asset.image_url" :alt="asset.name || '官方资产'" @error="markBoundAssetFailed(asset.id)" />
                    <div v-else class="generation-side-card__summary">{{ asset.image_url ? '官方图当前不可访问' : '暂无官方图' }}</div>
                    <div>
                      <strong>{{ asset.name || '未命名资产' }}</strong>
                      <p>{{ asset.prompt || asset.notes || '当前资产还没有补充说明。' }}</p>
                    </div>
                  </article>
                </div>
              </section>
            </div>
          </template>

          <template v-else>
            <div class="storyboard-generation-editor__grid">
              <section v-if="showVideoSummaryCard" class="storyboard-editor-card storyboard-editor-card--summary">
                <div class="storyboard-editor-card__head">
                  <div>
                    <label>镜头摘要</label>
                    <span>先确认镜头内容，再决定运动方式</span>
                  </div>
                </div>
                <div class="storyboard-editor-card__summary">
                  {{ activeSceneSummary || '当前镜头还没有补充描述' }}
                </div>
              </section>

              <section class="storyboard-editor-card storyboard-editor-card--prompt" :class="{ 'storyboard-editor-card--wide': !showVideoSummaryCard }">
                <div class="storyboard-editor-card__head">
                  <div>
                    <label>视频创意描述</label>
                    <span>集中写运动、表演、节奏和镜头切换</span>
                  </div>
                </div>
                <PromptAssistTextarea
                  v-model="scene.video_prompt"
                  field-label="视频创意描述"
                  assistant-title="视频提示词助手"
                  assistant-description="直接告诉系统你想补强的镜头运动、节奏推进、人物表演、声音氛围或画面重点，它会整理成更适合出视频的一版描述。"
                  :project-id="projectId"
                  stage="storyboard"
                  purpose="video_prompt"
                  :scene-number="scene.scene_number"
                  :rows="7"
                  :maxlength="1500"
                  placeholder="补充运动方式、节奏、表演、镜头切换等视频要求"
                  :mention-items="mentionItems"
                  allow-create-mention
                  @input="emit('mark-dirty')"
                  @create-mention-item="emit('create-mention-asset', $event)"
                />
              </section>

              <section class="storyboard-editor-card">
                <div class="storyboard-editor-card__head">
                  <div>
                    <label>镜头视频时长</label>
                    <span>单镜头 2-15 秒，优先沿用分镜建议</span>
                  </div>
                </div>
                <el-slider
                  v-model="scene.duration"
                  :min="2"
                  :max="15"
                  :step="1"
                  show-input
                  @input="emit('mark-dirty')"
                />
              </section>

              <section class="storyboard-editor-card">
                <div class="storyboard-editor-card__head">
                  <div>
                    <label>人工修改说明</label>
                    <span>仅在上传人工视频时填写，沉淀后续学习样本</span>
                  </div>
                </div>
                <el-input
                  v-model="sceneVideoFeedbackDrafts[scene.scene_number]"
                  type="textarea"
                  :rows="6"
                  maxlength="1000"
                  show-word-limit
                  placeholder="上传人工处理视频时，记录改了哪些点，供自成长智能体后续学习"
                />
              </section>
            </div>
          </template>
        </div>
      </div>

      <aside class="storyboard-generation-side">
        <article class="generation-side-card generation-side-card--workspace">
          <div class="generation-side-card__topbar">
            <div class="workspace-switcher workspace-switcher--compact">
              <button
                type="button"
                class="workspace-switcher__tab"
                :class="{ 'workspace-switcher__tab--active': generationTab === 'image' }"
                @click="emit('update:generationTab', 'image')"
              >
                图片生成
              </button>
              <button
                type="button"
                class="workspace-switcher__tab"
                :class="{ 'workspace-switcher__tab--active': generationTab === 'video' }"
                @click="emit('update:generationTab', 'video')"
              >
                视频生成
              </button>
            </div>
            <span class="generation-side-card__status">镜头 {{ scene.scene_number }}</span>
          </div>

          <div class="generation-side-card__hero">
            <div>
              <span class="generation-side-card__eyebrow">{{ generationTab === 'image' ? '单镜头出图' : '单镜头出视频' }}</span>
              <h3>{{ controlDeckTitle }}</h3>
              <p>
                {{ controlDeckDescription }}
              </p>
            </div>
            <div class="generation-side-card__hero-facts">
              <article>
                <span>当前镜头</span>
                <strong>{{ scene.scene_number }}</strong>
              </article>
              <article>
                <span>当前状态</span>
                <strong>{{ generationTab === 'image' ? (selectedImageUrl ? '已有图片' : '待出图') : (selectedVideoUrl ? '已有视频' : '待出视频') }}</strong>
              </article>
                <article>
                  <span>已绑官方资产</span>
                  <strong>{{ boundAssetsList.length || 0 }} 项</strong>
                </article>
                <article>
                  <span>工作重点</span>
                  <strong>{{ generationTab === 'image' ? '画面定版' : '运动定版' }}</strong>
                </article>
            </div>
          </div>

          <template v-if="generationTab === 'image'">
            <div class="generation-side-card__action-rack">
              <div class="generation-side-card__action-group">
                <div class="generation-side-card__action-head">
                  <label>素材操作</label>
                  <span>在素材库、上传和当前镜头结果之间快速切换</span>
                </div>
                <div class="generation-side-card__utility-actions">
                  <el-button plain @click="emit('open-knowledge-base-for-scene', scene.scene_number)">素材中心</el-button>
                  <el-button :loading="isSceneImageLoading(scene.scene_number, 'upload')" @click="emit('trigger-scene-file-upload', scene.scene_number)">上传图片</el-button>
                  <el-button
                    v-if="selectedImageUrl"
                    plain
                    @click="emit('download-scene-image', scene.scene_number)"
                  >
                    下载图片
                  </el-button>
                  <el-button
                    v-if="selectedImageUrl"
                    plain
                    :loading="isSceneImageLoading(scene.scene_number, 'clear')"
                    @click="emit('clear-scene-image', scene.scene_number)"
                  >
                    清空图片
                  </el-button>
                </div>
              </div>

              <div class="generation-side-card__action-group generation-side-card__action-group--accent">
                <div class="generation-side-card__action-head">
                  <label>生成动作</label>
                  <span>先选模型，再为当前镜头直接出图</span>
                </div>
                <div class="generation-side-card__primary-actions">
                  <GenerationModelSplitButton
                    class="generation-side-card__primary-split"
                    action-label="生成图片"
                    :options="imageModelOptions"
                    :model-value="selectedImageModelId"
                    :loading="isSceneImageLoading(scene.scene_number, 'generate')"
                    @update:model-value="handleUpdateImageModel"
                    @trigger="emit('generate-scene-image', { sceneNumber: scene.scene_number, imageModelId: selectedImageModelId || undefined })"
                  />
                  <el-button v-if="nextSceneNumber" plain @click="emit('select-scene', nextSceneNumber)">切到下一镜头</el-button>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <div v-if="selectedUploadedVideo" class="upload-summary-card">
              <strong>已上传人工视频</strong>
              <p>{{ selectedUploadedVideo.name || '手工处理视频' }}</p>
            </div>

            <div class="generation-side-card__action-rack">
              <div class="generation-side-card__action-group">
                <div class="generation-side-card__action-head">
                  <label>素材操作</label>
                  <span>上传人工版本或下载当前镜头视频结果</span>
                </div>
                <div class="generation-side-card__utility-actions generation-side-card__utility-actions--video">
                  <el-button :loading="sceneVideoLoading[scene.scene_number]" :disabled="isSceneVideoGenerating(scene.scene_number)" @click="emit('trigger-scene-video-upload', scene.scene_number)">上传视频</el-button>
                  <el-button :disabled="!selectedVideoUrl" @click="emit('download-scene-video', scene.scene_number)">
                    下载视频
                  </el-button>
                </div>
              </div>

              <div class="generation-side-card__action-group generation-side-card__action-group--accent">
                <div class="generation-side-card__action-head">
                  <label>生成动作</label>
                  <span>先确认时长和描述，再为当前镜头出视频</span>
                </div>
                <div class="generation-side-card__primary-actions">
                  <GenerationModelSplitButton
                    action-label="生成视频"
                    :options="videoModelOptions"
                    :model-value="selectedVideoModelId"
                    :loading="isSceneVideoGenerating(scene.scene_number)"
                    type="default"
                    @update:model-value="handleUpdateVideoModel"
                    @trigger="emit('generate-scene-video', { sceneNumber: scene.scene_number, videoModelId: selectedVideoModelId || undefined })"
                  />
                  <el-button v-if="nextSceneNumber" plain @click="emit('select-scene', nextSceneNumber)">切到下一镜头</el-button>
                </div>
              </div>
            </div>

            <div v-if="selectedVideoError" class="video-playback-warning">
              <strong>播放器兼容提示</strong>
              <p>{{ selectedVideoError }}</p>
            </div>

            <div class="generation-side-card__action-group">
              <div class="generation-side-card__action-head">
                <label>人工回填</label>
                <span>上传人工版本后，把修改说明沉淀回当前镜头</span>
              </div>
              <div class="generation-side-card__save-actions">
                <el-button class="generation-side-card__primary-button" type="primary" :loading="sceneVideoSaving[scene.scene_number]" @click="emit('save-scene-video-feedback', scene.scene_number)">
                    保存视频说明
                </el-button>
              </div>
            </div>
          </template>
        </article>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import GenerationModelSplitButton from '@/components/GenerationModelSplitButton.vue'
import PromptAssistTextarea from '@/components/PromptAssistTextarea.vue'
import ReferenceLinkActions from '@/components/ReferenceLinkActions.vue'
import { useModelCatalogStore } from '@/stores/modelCatalog'
import { resolveGenerationModelSelection, setGenerationModelSelection } from '@/utils/generationModelSelection'

import type {
  ConsistencyAssetDraft,
  PromptMentionOption,
  StoryboardGenerationSceneNavigatorItem,
  StoryboardSceneDraft,
  UploadedWorkspaceVideo,
} from '@/views/customer/generation-workspace/types'

type GenerationTab = 'image' | 'video'
type SceneImageAction = 'upload' | 'clear' | 'generate'

const props = defineProps<{
  generationTab: GenerationTab
  projectId: number
  scene: StoryboardSceneDraft | null
  selectedImageUrl: string
  selectedVideoUrl: string
  selectedUploadedVideo: UploadedWorkspaceVideo | null
  selectedVideoError: string
  previewOverlayVisible: boolean
  previewOverlayTitle: string
  previewOverlayDescription: string
  previewOverlayNote: string
  sceneNavigatorItems: StoryboardGenerationSceneNavigatorItem[]
  boundAssets: {
    character: ConsistencyAssetDraft[]
    scene: ConsistencyAssetDraft[]
    prop: ConsistencyAssetDraft[]
  }
  selectedSceneNumber: number | null
  mentionItems: PromptMentionOption[]
  storyboardSaving: boolean
  sceneVideoTask: Record<string, any> | null
  sceneVideoTaskDescription: string
  sceneVideoTaskStatusLabel: string
  sceneVideoTaskTimeline: Array<{ label: string; state: string }>
  sceneVideoTaskFacts: Array<{ label: string; value: string }>
  sceneVideoFeedbackDrafts: Record<number, string>
  sceneVideoLoading: Record<number, boolean>
  sceneVideoSaving: Record<number, boolean>
  isSceneImageLoading: (sceneNumber: number, action: SceneImageAction) => boolean
  isSceneImageBusy: (sceneNumber: number) => boolean
  isSceneVideoGenerating: (sceneNumber: number) => boolean
}>()

const emit = defineEmits<{
  'update:generationTab': [value: GenerationTab]
  'mark-dirty': []
  'create-mention-asset': [payload: { name: string; sceneNumber?: number }]
  'select-scene': [sceneNumber: number]
  'download-scene-image': [sceneNumber: number]
  'open-knowledge-base-for-scene': [sceneNumber: number]
  'trigger-scene-file-upload': [sceneNumber: number]
  'clear-scene-image': [sceneNumber: number]
  'generate-scene-image': [payload: { sceneNumber: number; imageModelId?: string }]
  'scene-video-loaded': [sceneNumber: number]
  'scene-video-error': [sceneNumber: number]
  'generate-scene-video': [payload: { sceneNumber: number; videoModelId?: string }]
  'trigger-scene-video-upload': [sceneNumber: number]
  'download-scene-video': [sceneNumber: number]
  'save-scene-video-feedback': [sceneNumber: number]
}>()

const modelCatalogStore = useModelCatalogStore()
const selectedImageModelId = ref('')
const selectedVideoModelId = ref('')

const imageModelOptions = computed(() => modelCatalogStore.imageModels())
const videoModelOptions = computed(() => modelCatalogStore.videoModels())
const activeSceneStatusLabel = computed(() => props.generationTab === 'image'
  ? (props.selectedImageUrl ? '已有图片' : '待出图')
  : (props.selectedVideoUrl ? '已有视频' : '待出视频'))
const previewStageLabel = computed(() => props.generationTab === 'image' ? '镜头画面' : '镜头视频')
const previewHeadline = computed(() => selectedSceneItem.value?.title || `镜头 ${activeSceneNumber.value || props.scene?.scene_number || ''}`)
const previewSubline = computed(() => props.generationTab === 'image'
  ? '优先确认主体、构图和光线，再推进生成。'
  : '优先确认运动、节奏和时长，再推进生成。')
const activeSceneSummary = computed(() => String(props.scene?.action || props.scene?.description || '').trim())
const controlDeckTitle = computed(() => props.generationTab === 'image' ? '图片控制台' : '视频控制台')
const controlDeckDescription = computed(() => props.generationTab === 'image'
  ? '围绕当前镜头的画面定版工作展开：从素材、上传、模型到生成动作都在这里收口。'
  : '围绕当前镜头的运动定版工作展开：先校准描述与时长，再决定生成或上传人工版本。')

function normalizeAssistantComparableText(value: unknown) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/[：:]/g, ':')
    .trim()
}

const showImageSummaryCard = computed(() => {
  const summary = normalizeAssistantComparableText(activeSceneSummary.value)
  const prompt = normalizeAssistantComparableText(props.scene?.background_prompt)
  if (!summary) {
    return false
  }
  if (!prompt) {
    return true
  }
  return summary !== prompt
})

const showVideoSummaryCard = computed(() => {
  const summary = normalizeAssistantComparableText(activeSceneSummary.value)
  const prompt = normalizeAssistantComparableText(props.scene?.video_prompt)
  if (!summary) {
    return false
  }
  if (!prompt) {
    return true
  }
  return summary !== prompt
})

function handleUpdateImageModel(value: string) {
  selectedImageModelId.value = value
  setGenerationModelSelection('image', 'storyboard_image_generate', value)
}

function handleUpdateVideoModel(value: string) {
  selectedVideoModelId.value = value
  setGenerationModelSelection('video', 'storyboard_video_generate', value)
}

watch(imageModelOptions, (options) => {
  if (!options.length) {
    selectedImageModelId.value = ''
    return
  }
  if (!selectedImageModelId.value || !options.some((item) => item.id === selectedImageModelId.value)) {
    selectedImageModelId.value = resolveGenerationModelSelection('image', 'storyboard_image_generate', options)
  }
}, { immediate: true })

watch(videoModelOptions, (options) => {
  if (!options.length) {
    selectedVideoModelId.value = ''
    return
  }
  if (!selectedVideoModelId.value || !options.some((item) => item.id === selectedVideoModelId.value)) {
    selectedVideoModelId.value = resolveGenerationModelSelection('video', 'storyboard_video_generate', options)
  }
}, { immediate: true })

onMounted(() => {
  void modelCatalogStore.ensureLoaded()
})

const boundAssetsList = computed(() => [
  ...props.boundAssets.character,
  ...props.boundAssets.scene,
  ...props.boundAssets.prop,
])
const activeSceneNumber = computed(() => props.scene?.scene_number || props.selectedSceneNumber || props.sceneNavigatorItems[0]?.scene_number || null)
const selectedSceneIndex = computed(() => {
  const sceneNumber = activeSceneNumber.value
  const index = props.sceneNavigatorItems.findIndex((item) => item.scene_number === sceneNumber)
  return index >= 0 ? index : 0
})
const selectedSceneItem = computed(() => props.sceneNavigatorItems[selectedSceneIndex.value] || null)
const previousSceneNumber = computed(() => props.sceneNavigatorItems[selectedSceneIndex.value - 1]?.scene_number || null)
const nextSceneNumber = computed(() => props.sceneNavigatorItems[selectedSceneIndex.value + 1]?.scene_number || null)

const selectedImageFailed = ref(false)
const selectedImageRetryCount = ref(0)
const failedBoundAssets = ref<Record<string, boolean>>({})
const selectedImagePreviewKey = computed(() => `${props.generationTab}:${props.selectedImageUrl || ''}:${selectedImageRetryCount.value}`)
const stableSelectedVideoUrl = ref('')

function buildMediaAssetIdentity(url: string) {
  const normalized = String(url || '').trim()
  if (!normalized) {
    return ''
  }

  try {
    const parsed = new URL(normalized)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return normalized.split('?')[0].trim()
  }
}

watch(
  () => `${props.generationTab}::${props.selectedImageUrl}`,
  () => {
    selectedImageFailed.value = false
    selectedImageRetryCount.value = 0
  }
)

watch(
  () => ({
    sceneNumber: activeSceneNumber.value,
    nextUrl: String(props.selectedVideoUrl || '').trim(),
  }),
  ({ sceneNumber, nextUrl }, previous) => {
    if (!nextUrl) {
      stableSelectedVideoUrl.value = ''
      return
    }

    const nextIdentity = buildMediaAssetIdentity(nextUrl)
    const currentIdentity = buildMediaAssetIdentity(stableSelectedVideoUrl.value)
    const sceneChanged = sceneNumber !== previous?.sceneNumber

    if (sceneChanged || !stableSelectedVideoUrl.value || nextIdentity !== currentIdentity) {
      stableSelectedVideoUrl.value = nextUrl
    }
  },
  { immediate: true }
)

watch(
  () => boundAssetsList.value.map((asset) => `${asset.id}:${asset.image_url || ''}`).join('|'),
  () => {
    failedBoundAssets.value = {}
  }
)

function markBoundAssetFailed(id: string) {
  failedBoundAssets.value = {
    ...failedBoundAssets.value,
    [id]: true,
  }
}

function handleSelectedImageError() {
  if (selectedImageRetryCount.value < 1) {
    selectedImageRetryCount.value += 1
    return
  }

  selectedImageFailed.value = true
}

function handleSelectedVideoError(sceneNumber: number) {
  const nextUrl = String(props.selectedVideoUrl || '').trim()
  if (nextUrl && nextUrl !== stableSelectedVideoUrl.value) {
    stableSelectedVideoUrl.value = nextUrl
  }
  emit('scene-video-error', sceneNumber)
}

function getSceneStatusLabel(item: StoryboardGenerationSceneNavigatorItem) {
  if (props.generationTab === 'video') {
    return item.video_ready ? '已有视频' : '待处理视频'
  }
  return item.image_ready ? '已绑定图片' : '待处理图片'
}
</script>

<style scoped src="./StoryboardGenerationWorkspace.css"></style>